import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Navbar } from "@/components/Navbar";
import { ComplianceFooter } from "@/components/Compliance";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, Download, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const { user, updateProfile, changePassword } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pwError, setPwError] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const hasAccess = user?.has_access || user?.role === "admin";
  const isLocal = user?.auth_provider === "local";

  const saveName = async (e) => {
    e.preventDefault();
    setSavingName(true);
    try {
      await updateProfile(name);
      toast.success("Name updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update");
    }
    setSavingName(false);
  };

  const savePw = async (e) => {
    e.preventDefault();
    setPwError("");
    if (next !== confirm) {
      setPwError("New passwords do not match");
      return;
    }
    setSavingPw(true);
    try {
      await changePassword(current, next);
      toast.success("Password changed");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      setPwError(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password");
    }
    setSavingPw(false);
  };

  const downloadWorksheets = () => {
    window.open(`${BACKEND_URL}/api/me/worksheets/pdf`, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="profile-page">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Profile & settings</h1>

        {/* Account status */}
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-display font-semibold text-slate-900 mb-3">Account</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="text-slate-800 font-medium">{user?.email}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Sign-in method</span><span className="text-slate-800 font-medium capitalize">{user?.auth_provider}</span></div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Course access</span>
              {hasAccess ? (
                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-0" data-testid="purchase-status"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Active</Badge>
              ) : (
                <Badge className="bg-slate-200 text-slate-600 hover:bg-slate-200 border-0" data-testid="purchase-status">Not purchased</Badge>
              )}
            </div>
          </div>
          {!hasAccess && (
            <Button className="mt-4 bg-slate-900 hover:bg-slate-800" onClick={() => navigate("/dashboard")} data-testid="profile-buy-button">Unlock the course</Button>
          )}
        </div>

        {/* Name */}
        <form onSubmit={saveName} className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
          <h2 className="font-display font-semibold text-slate-900">Your name</h2>
          <div className="space-y-1.5">
            <Label htmlFor="name">Display name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
          </div>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={savingName} data-testid="profile-save-name-button">
            {savingName ? "Saving…" : "Save name"}
          </Button>
        </form>

        {/* Password */}
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-display font-semibold text-slate-900 mb-3">Password</h2>
          {isLocal ? (
            <form onSubmit={savePw} className="space-y-4" data-testid="change-password-form">
              {pwError && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="change-password-error">{pwError}</p>}
              <div className="space-y-1.5">
                <Label htmlFor="cur">Current password</Label>
                <Input id="cur" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="current-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new">New password</Label>
                <Input id="new" type="password" value={next} onChange={(e) => setNext(e.target.value)} required data-testid="new-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="conf">Confirm new password</Label>
                <Input id="conf" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="confirm-password-input" />
              </div>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={savingPw} data-testid="change-password-button">
                {savingPw ? "Saving…" : "Change password"}
              </Button>
            </form>
          ) : (
            <p className="text-sm text-slate-500 flex items-center gap-2"><Lock className="w-4 h-4" /> You sign in with Google, so there's no password to manage here.</p>
          )}
        </div>

        {/* Worksheets export */}
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-display font-semibold text-slate-900 mb-1">Your worksheets</h2>
          <p className="text-sm text-slate-500 mb-4">Download everything you've filled in across all modules as a single PDF workbook.</p>
          <Button variant="outline" onClick={downloadWorksheets} disabled={!hasAccess} data-testid="download-worksheets-button">
            <Download className="w-4 h-4 mr-2" /> Download workbook (PDF)
          </Button>
        </div>
      </main>
      <ComplianceFooter />
    </div>
  );
}
